/**
 * Klasse DIKTAT_FLOW_AUTH_OR_SKIP — Diktat-Durchstich (Mic + Element-Load + STT + Analyse).
 * Polar: VERBOTEN Fake-„Analyse läuft“ ohne Transkript/Token/Webhook;
 *        LEX-Auto-Join aus Diktat-Floskel; Phone-Sync vortäuschen ohne Homeserver.
 *        ERLAUBT MediaRecorder + Datei (auch Element-Export), Whisper DIKTAT_UPLOAD,
 *        bestehende Hintergrundanalyse (frisches Fenster / Chunks).
 *
 * Klasse DIKTAT_ARCHIVE_IN_TAB — Transkripte im Diktat-Reiter, nicht in leere Dokumente.
 * Polar: VERBOTEN in Dokumente schicken, wenn die Datei nicht in der Explorer-Liste
 *        der offenen Akte liegt; VERBOTEN kanzleiweites zweites Filter/Inventar neben PB;
 *        akte_id nicht erfinden. ERLAUBT Ordner „Diktate“; Klick öffnet das Transkript hier.
 *
 * Klasse DIKTAT_LIST_PB_PERSIST — Diktat-Ordner aus derselben Quelle wie Dokumente.
 * Polar: VERBOTEN Buttons disabled obwohl PDF/Record da ist (nur weil akte_id fehlt
 *        oder Session-Flag); VERBOTEN Reload leer obwohl PB-Record existiert;
 *        VERBOTEN Session-only-Liste als „PocketBase“ beschriften; VERBOTEN Fake-Aktionen
 *        ohne Datei; VERBOTEN zweites kanzleiweites Filter; akte_id nicht erfinden.
 *        ERLAUBT Öffnen/Bearbeiten/Löschen/Share/Akte wie Explorer sobald nextcloud_path
 *        oder PB-id da ist; ohne Akte Zuordnen anbieten, nicht die anderen Icons grauen;
 *        Mount lädt dieselben Diktat-Records (Filter: /protokolle/, Ordner Diktate/
 *        audio-analyse, Analyse_*.txt oder Legacy-Dateiname — nicht „nur aktuelle Akte“).
 *        Ordner im Diktat-Reiter bleibt sichtbar (auch leer nach Reload).
 *
 * Klasse DIKTAT_EDIT_WITH_PATH — Inhalt bearbeiten (OO/Editor) unabhängig von akte_id.
 * Polar: VERBOTEN Bearbeiten grau nur weil akte_id fehlt, obwohl nextcloud_path + PB-id
 *        da sind; VERBOTEN OnlyOffice auf Datei ohne Pfad vortäuschen.
 *        ERLAUBT dieselbe Edit/Open-in-OO-Aktion wie Explorer, sobald der Record einen
 *        erlaubten NC-Pfad hat. Rename darf an Folder-API (portal-documents) gebunden
 *        bleiben — Inhalt bearbeiten nicht. „Keine Akte — anlegen“ bleibt ehrlich,
 *        blockt aber nicht den Editor.
 *
 * Klasse DIKTAT_OUTPUT_FORMAT — nach Transkript Format wählen, dann passend öffnen.
 * Polar: VERBOTEN OnlyOffice so tun als würde es .txt zuverlässig editieren.
 *        VERBOTEN DOCX/PDF mit Platzhalter statt vorhandenem Sitzungs-Transkript.
 *        VERBOTEN Overflow hidden am Ordner, das das Formatmenü clippt.
 *        VERBOTEN SSRF-Allowlist aufmachen; neues Microservice; Donna; Live-Portal.
 *        ERLAUBT Transkript aus Session (und PB/NC-Text) in DOCX + Gotenberg-PDF
 *        in der bestehenden Orbis-Shell. Popover flip nach oben oder Portal/Overlay
 *        (POPOVER_DISMISS_OUTSIDE). PDF-400 ehrlich (DIKTAT_UPLOAD), nicht Telemetry jagen.
 */

import { fileExtensionFromLeaf, portalDocumentBasename } from './portalFileType.ts';
import { buildElementRoomUrl, isHonestMatrixRoomId } from './portalMatrixOrigins.ts';

export const DIKTAT_TRANSCRIPT_MIN_CHARS = 24;
/** Shell-Platzhalter — kein Transkript; darf nicht als DOCX/PDF-Body durchgehen. */
export const DIKTAT_PLACEHOLDER_LINE = 'Kein Analyse-Text vom Server erhalten.';
export const DIKTAT_FORMAT_MENU_WIDTH_PX = 224;
export const DIKTAT_FORMAT_MENU_EST_HEIGHT_PX = 200;
export const DIKTAT_FORMAT_MENU_GAP_PX = 6;
export const DIKTAT_FORMAT_MENU_MARGIN_PX = 8;
/** Klasse DIKTAT_OUTPUT_FORMAT — Reihenfolge im Menü: Weiterarbeiten zuerst. */
export const DIKTAT_OUTPUT_FORMATS = ['docx', 'pdf', 'txt'] as const;
export type DiktatOutputFormat = (typeof DIKTAT_OUTPUT_FORMATS)[number];
export const DIKTAT_OUTPUT_FORMAT_DEFAULT: DiktatOutputFormat = 'docx';
export const DIKTAT_OUTPUT_MIME: Record<DiktatOutputFormat, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain;charset=utf-8',
};

export type DiktatEditAction =
  | 'onlyoffice'
  | 'pdf_viewer'
  | 'plaintext'
  | 'pick_format'
  | 'blocked';

export type DiktatFileKind = 'docx' | 'pdf' | 'txt' | 'other';

/** Audio-Drop aufs Diktat-Fenster — bestehende Whisper-Pipeline, kein Phone-Sync. */
export const DIKTAT_DROP_AUDIO_EXTS = [
  'm4a',
  'mp3',
  'wav',
  'ogg',
  'webm',
  'aac',
  'opus',
  'oga',
  'flac',
] as const;

export function diktatDropLooksLikeAudio(name?: string, mime?: string): boolean {
  const m = String(mime || '').toLowerCase();
  if (m.startsWith('audio/')) return true;
  const ext = fileExtensionFromLeaf(name || '');
  return (DIKTAT_DROP_AUDIO_EXTS as readonly string[]).includes(ext);
}

export function diktatDroppedAudioFile(
  files: ArrayLike<{ name?: string; type?: string }> | null | undefined,
): { name: string; type: string } | null {
  const list = files ? Array.from(files) : [];
  for (const file of list) {
    if (!diktatDropLooksLikeAudio(file.name, file.type)) continue;
    return { name: String(file.name || ''), type: String(file.type || '') };
  }
  return null;
}

export function parseDiktatOutputFormat(
  raw: string | null | undefined,
): DiktatOutputFormat | null {
  const value = String(raw || '')
    .trim()
    .toLowerCase();
  if (value === 'docx' || value === 'pdf' || value === 'txt') return value;
  return null;
}

export function diktatOutputFormatLabel(format: DiktatOutputFormat): string {
  if (format === 'docx') return 'DOCX';
  if (format === 'pdf') return 'PDF';
  return 'TXT';
}

export function diktatOutputFormatHint(format: DiktatOutputFormat): string {
  if (format === 'docx') return 'Weiterarbeiten in OnlyOffice';
  if (format === 'pdf') return 'Protokoll-Viewer';
  return 'Klartext-Pane';
}

export function diktatFileKind(
  filename?: string | null,
  nextcloudPath?: string | null,
): DiktatFileKind {
  const ext =
    fileExtensionFromLeaf(filename || '') ||
    fileExtensionFromLeaf(portalDocumentBasename(nextcloudPath || ''));
  if (ext === 'docx' || ext === 'doc' || ext === 'odt' || ext === 'rtf') return 'docx';
  if (ext === 'pdf') return 'pdf';
  if (ext === 'txt' || ext === 'md' || ext === 'markdown' || ext === 'csv') return 'txt';
  return 'other';
}

export function diktatOutputFileName(args: {
  format: DiktatOutputFormat;
  sourceName?: string | null;
  now?: Date;
}): string {
  const now = args.now || new Date();
  const stamp = now.toISOString().slice(0, 10);
  const ts = String(now.getTime()).slice(-6);
  if (args.format === 'pdf') return `Protokoll_${stamp}_${ts}.pdf`;
  if (args.format === 'docx') return `Protokoll_${stamp}_${ts}.docx`;
  const base = String(args.sourceName || 'Diktat')
    .replace(/\.[^.]+$/, '')
    .replace(/[^\wäöüÄÖÜß.-]+/g, '_')
    .slice(0, 48);
  return `Analyse_${base || 'Diktat'}_${stamp}.txt`;
}

/**
 * Convert-only an DIKTAT_UPLOAD: nur PDF über Gotenberg, ohne Audio/Whisper.
 * DOCX/TXT entstehen im Portal über PORTAL_DOCUMENT_UPLOAD — nicht hier vortäuschen.
 */
export function diktatConvertOnlyDecision(args: {
  hasAudioFile: boolean;
  outputFormat?: string | null;
  sourceText?: string | null;
}): { ok: true; convertOnly: boolean; format?: DiktatOutputFormat } | { ok: false; message: string } {
  const format = parseDiktatOutputFormat(args.outputFormat);
  const text = String(args.sourceText || '').trim();
  if (args.hasAudioFile) {
    if (format && format !== 'pdf') {
      return { ok: false, message: 'Audio-Upload erzeugt das Transkript — Format danach im Menü wählen.' };
    }
    return { ok: true, convertOnly: false };
  }
  if (!format) {
    return { ok: false, message: 'Keine Audio-Datei empfangen.' };
  }
  if (format !== 'pdf') {
    return {
      ok: false,
      message: 'Gotenberg-Konvertierung nur für PDF. DOCX und TXT über das Format-Menü.',
    };
  }
  if (text.length < DIKTAT_TRANSCRIPT_MIN_CHARS) {
    return { ok: false, message: 'Kein Text für die PDF-Konvertierung.' };
  }
  return { ok: true, convertOnly: true, format: 'pdf' };
}

export function diktatMaterializeGuard(args: {
  format: DiktatOutputFormat;
  bodyText: string;
  existingPdfUrl?: string | null;
}): { ok: true } | { ok: false; message: string } {
  if (args.format === 'pdf' && String(args.existingPdfUrl || '').trim()) {
    return { ok: true };
  }
  if (!diktatUsableTranscript(args.bodyText)) {
    return {
      ok: false,
      message: 'Kein Transkript für dieses Format — Datei wird nicht erzeugt.',
    };
  }
  return { ok: true };
}
export const DIKTAT_ANALYSIS_EXTRACT_SOURCE = 'diktat_transcript';
/** Gleicher n8n-Pfad wie `portalAudioAnalyze` / nginx auth_request. */
export const DIKTAT_UPLOAD_WEBHOOK_PATH = '/webhook/diktat-upload';

/**
 * Klasse DIKTAT_UPLOAD_SESSION_OK — Fetch-Header für Whisper-Upload.
 * Polar: VERBOTEN unauth POST → 200; VERBOTEN eingeloggter User ohne Bearer
 * (Protected-Listen-Drift: `portalAuthzHeaders` würde sonst `{}` liefern).
 * ERLAUBT PB-Bearer wie Dokument-Upload; nginx setzt X-Arkorbit-Customer-Id.
 */
export function diktatUploadSessionHeaders(args: {
  token: string | null | undefined;
  listedAuthzHeaders?: Record<string, string>;
}): Record<string, string> {
  const listed = String(args.listedAuthzHeaders?.Authorization || '').trim();
  if (listed) {
    return {
      Authorization: listed.toLowerCase().startsWith('bearer ')
        ? listed
        : `Bearer ${listed}`,
    };
  }
  const token = String(args.token || '').trim();
  if (!token) return {};
  return {
    Authorization: token.toLowerCase().startsWith('bearer ')
      ? token
      : `Bearer ${token}`,
  };
}

export function diktatUploadHasSessionBearer(
  headers: Record<string, string>,
): boolean {
  return /^Bearer\s+\S/i.test(String(headers.Authorization || ''));
}

export function diktatTranscriptIsAnalyzable(text: string | null | undefined): boolean {
  return (text || '').trim().length >= DIKTAT_TRANSCRIPT_MIN_CHARS;
}

export function pickDiktatTranscript(diktat: {
  transcriptText?: string | null;
  protokollText?: string | null;
}): string {
  const whisperRaw = (diktat.transcriptText || '').trim();
  const protoRaw = (diktat.protokollText || '').trim();
  const whisper = diktatUsableTranscript(whisperRaw);
  if (whisper) return whisper;
  const proto = diktatUsableTranscript(protoRaw);
  if (proto) return proto;
  if (whisperRaw) return whisperRaw;
  return protoRaw;
}

function unwrapDiktatPayload(raw: unknown): Record<string, unknown> | null {
  let node: unknown = raw;
  if (typeof node === 'string') {
    const trimmed = node.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
    try {
      node = JSON.parse(trimmed) as unknown;
    } catch {
      return null;
    }
  }
  if (Array.isArray(node)) node = node[0];
  if (!node || typeof node !== 'object') return null;
  let rec = node as Record<string, unknown>;
  const json = rec.json;
  if (json && typeof json === 'object' && !Array.isArray(json)) rec = json as Record<string, unknown>;
  const data = rec.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const inner = data as Record<string, unknown>;
    if (inner.transcriptText || inner.transcript || inner.text || inner.protokollText) rec = inner;
  }
  return rec;
}

function firstDiktatString(rec: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = rec[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function diktatSegmentText(rec: Record<string, unknown>): string {
  if (!Array.isArray(rec.segments)) return '';
  return rec.segments
    .map((row) => {
      if (!row || typeof row !== 'object') return '';
      return String((row as Record<string, unknown>).text || '').trim();
    })
    .filter(Boolean)
    .join(' ')
    .trim();
}

function collectDiktatTranscript(rec: Record<string, unknown>): string {
  const direct = firstDiktatString(rec, [
    'transcriptText',
    'transcript',
    'text',
    'whisper_text',
    'transcription',
  ]);
  if (direct) return direct;
  const result = rec.result;
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    const nested = collectDiktatTranscript(result as Record<string, unknown>);
    if (nested) return nested;
  }
  return diktatSegmentText(rec);
}

/**
 * Whisper-Antwort kann `text` heißen oder in `json`/`data` stecken.
 * Leeres `transcriptText` darf ein echtes Transkript daneben nicht verschlucken.
 */
export function readDiktatUploadFields(raw: unknown): {
  transcriptText: string;
  protokollText: string;
} {
  const rec = unwrapDiktatPayload(raw);
  if (!rec) return { transcriptText: '', protokollText: '' };
  const bags: Record<string, unknown>[] = [rec];
  const data = rec.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    bags.push(data as Record<string, unknown>);
  }
  let transcriptText = '';
  let protokollText = '';
  for (const bag of bags) {
    if (!transcriptText) transcriptText = collectDiktatTranscript(bag);
    if (!protokollText) {
      protokollText = firstDiktatString(bag, ['protokollText', 'protokoll', 'protocol']);
    }
  }
  return { transcriptText, protokollText };
}

const DIKTAT_TRANSCRIPT_KEYS = [
  'transcriptText',
  'transcript',
  'text',
  'whisper_text',
  'transcription',
  'protokollText',
  'protokoll',
  'transcriptLength',
  'segments',
] as const;

/**
 * Nur eine echte Transkript-Antwort (Felder da, auch leer) zählt als „kein Text“.
 * `{ ok: true }` vom Normalizer ohne diese Felder ist ein abgebrochener Lauf.
 */
export function diktatUploadIsTranscriptResult(raw: unknown): boolean {
  const rec = unwrapDiktatPayload(raw);
  if (!rec) return false;
  if (rec.error === true || rec.ok === false) return false;
  const hasField = DIKTAT_TRANSCRIPT_KEYS.some((key) => key in rec);
  if (hasField && rec.ok !== false) return true;
  const fields = readDiktatUploadFields(raw);
  return Boolean(fields.transcriptText || fields.protokollText);
}

/**
 * Text in der Diktat-Fläche zum Kopieren.
 * Auswertung nur, wenn das Protokoll vom Transkript abweicht.
 * Kein Chat, keine automatische Analyse-Datei als einziger Weg.
 */
export function diktatSurfaceCopyBlocks(args: {
  transcriptText?: string | null;
  protokollText?: string | null;
}): { transcript: string; auswertung: string } {
  const transcript = (args.transcriptText || '').trim();
  const proto = (args.protokollText || '').trim();
  const auswertung = proto && proto !== transcript ? proto : '';
  return { transcript, auswertung };
}

export function diktatNameStem(name?: string | null): string {
  return String(name || '')
    .replace(/\.[^.]+$/, '')
    .replace(/^(Analyse_|Protokoll_)/i, '')
    .replace(/[_-]?\d{4}-\d{2}-\d{2}.*$/, '')
    .replace(/[^\wäöüÄÖÜß]+/g, '')
    .toLowerCase();
}

/** Roher Transkript-String — ohne Shell-Platzhalter, ohne leere Metadaten-Hülle. */
export function diktatUsableTranscript(text: string | null | undefined): string {
  let raw = String(text || '').trim();
  if (!raw) return '';
  if (raw.includes(DIKTAT_PLACEHOLDER_LINE)) {
    raw = raw.split(DIKTAT_PLACEHOLDER_LINE).join('\n');
  }
  const stripChrome = (value: string): string =>
    value
      .replace(/^Audio-Analyse:.*$/gm, '')
      .replace(/^Erstellt:.*$/gm, '')
      .replace(/^---+\s*$/gm, '')
      .replace(/KI-generiert — vor rechtsrelevanter Verwendung prüfen\.?/g, '')
      .trim();
  const whisperParts = raw.split(/=== Transkript \(Whisper\) ===/i);
  if (whisperParts.length > 1) {
    const whisper = stripChrome(
      whisperParts.slice(1).join('\n').replace(/===+[^=]*===+/g, '\n'),
    );
    if (diktatTranscriptIsAnalyzable(whisper)) return whisper;
  }
  const protoParts = raw.split(/=== Protokoll(?: \/ Analyse)? ===/i);
  if (protoParts.length > 1) {
    const proto = stripChrome(protoParts[1].split(/===/)[0]);
    if (diktatTranscriptIsAnalyzable(proto)) return proto;
  }
  const stripped = stripChrome(raw);
  return diktatTranscriptIsAnalyzable(stripped) ? stripped : '';
}

export function resolveDiktatOutputTranscript(args: {
  resultTranscript?: string | null;
  entry?: Pick<DiktatArchiveEntry, 'transcriptText' | 'sourceName' | 'filename' | 'pb_record_id'> | null;
  archive?: readonly DiktatArchiveEntry[];
  /** Hydrate: nur pb_id/Stamm (50+). Formatwahl: 1 = beliebige Session-Zeile als Fallback. */
  minScore?: number;
}): string {
  const fromEntry = diktatUsableTranscript(args.entry?.transcriptText);
  if (fromEntry) return fromEntry;
  const fromResult = diktatUsableTranscript(args.resultTranscript);
  if (fromResult) return fromResult;
  const archive = args.archive || [];
  const minScore = args.minScore ?? 1;
  const entryPb = String(args.entry?.pb_record_id || '').trim();
  const stem = diktatNameStem(args.entry?.filename || args.entry?.sourceName);
  let best: { text: string; score: number; ts: number } | null = null;
  for (const row of archive) {
    const text = diktatUsableTranscript(row.transcriptText);
    if (!text) continue;
    let score = 1;
    if (entryPb && String(row.pb_record_id || '').trim() === entryPb) score = 100;
    else if (stem && diktatNameStem(row.filename || row.sourceName) === stem) score = 50;
    const ts = Date.parse(row.createdAt) || 0;
    if (!best || score > best.score || (score === best.score && ts > best.ts)) {
      best = { text, score, ts };
    }
  }
  if (best && best.score >= minScore) return best.text;
  return '';
}

export function placeDiktatFormatMenu(
  anchor: { left: number; top: number; right: number; bottom: number },
  viewport: { width: number; height: number },
  align: 'left' | 'right' = 'right',
): { left: number; top: number; flipUp: boolean } {
  const width = DIKTAT_FORMAT_MENU_WIDTH_PX;
  const height = DIKTAT_FORMAT_MENU_EST_HEIGHT_PX;
  const gap = DIKTAT_FORMAT_MENU_GAP_PX;
  const margin = DIKTAT_FORMAT_MENU_MARGIN_PX;
  const spaceBelow = viewport.height - anchor.bottom - gap - margin;
  const spaceAbove = anchor.top - gap - margin;
  const flipUp = spaceBelow < height && spaceAbove > spaceBelow;
  const maxLeft = Math.max(margin, viewport.width - width - margin);
  let left = align === 'left' ? anchor.left : anchor.right - width;
  left = Math.min(Math.max(left, margin), maxLeft);
  let top = flipUp ? anchor.top - height - gap : anchor.bottom + gap;
  const maxTop = Math.max(margin, viewport.height - height - margin);
  top = Math.min(Math.max(top, margin), maxTop);
  return { left, top, flipUp };
}

/**
 * Extract-Format wie Dokument-Hintergrundanalyse (`Auszug (UTF-8…)`),
 * damit hasUsableAkteExtract + Chunk-Plan greifen — kein zweites Analyse-Produkt.
 */
export function buildDiktatAnalysisDocumentContext(args: {
  sourceName: string;
  transcriptText: string;
  protokollText?: string;
}): string {
  const transcript = args.transcriptText.trim();
  const proto = (args.protokollText || '').trim();
  const body: string[] = [];
  if (proto && proto !== transcript) {
    body.push('=== Protokoll ===', proto, '');
  }
  body.push('=== Transkript (Whisper) ===', transcript);
  const name = (args.sourceName || 'Diktat').trim() || 'Diktat';
  return [
    `Dokument: ${name}`,
    'Quelle: Diktat / Whisper',
    '',
    'Auszug (UTF-8, Extract-Cache):',
    body.join('\n'),
  ].join('\n');
}

export type DiktatAnalysisDecision =
  | { ok: true }
  | { ok: false; reason: string };

export const DIKTAT_ANALYSIS_NEEDS_DICTATION =
  'Zuerst diktieren. Ohne Transkript startet keine Auswertung.';

/** Datei kam an, Whisper hat keinen Text geliefert. Kein erfundenes Transkript. */
export const DIKTAT_UPLOAD_NO_TEXT =
  'Die Datei ist angekommen. Es wurde kein Text erkannt.';

/** Lauf ist nicht als Transkript-Antwort angekommen. Nicht als „keine Sprache“ verkaufen. */
export const DIKTAT_UPLOAD_FAILED =
  'Die Transkription ist fehlgeschlagen. Es wurde kein Text geliefert.';

export function diktatAnalysisStartDecision(args: {
  transcript: string;
  hasAuthToken: boolean;
  webhookUrl: string;
  /** true, wenn in diesem Lauf eine Audiodatei ankam. */
  fileReceived?: boolean;
}): DiktatAnalysisDecision {
  if (!args.hasAuthToken) {
    return {
      ok: false,
      reason: 'Kein Portal-Token — bitte neu anmelden. Analyse startet nicht ohne Auth.',
    };
  }
  if (!String(args.webhookUrl || '').trim()) {
    return {
      ok: false,
      reason: 'ARKO-Webhook fehlt — Analyse nicht gestartet.',
    };
  }
  if (!diktatTranscriptIsAnalyzable(diktatUsableTranscript(args.transcript))) {
    return {
      ok: false,
      reason: args.fileReceived ? DIKTAT_UPLOAD_NO_TEXT : DIKTAT_ANALYSIS_NEEDS_DICTATION,
    };
  }
  return { ok: true };
}

export type ElementDiktatLoadKind =
  | 'manual_file'
  | 'blocked_no_room'
  | 'blocked_no_homeserver';

export type ElementDiktatLoadAssessment = {
  kind: ElementDiktatLoadKind;
  canPickAudioFile: boolean;
  /** Portal kann E2EE-Sprachnachrichten nicht still aus dem Raum ziehen. */
  liveRoomPull: boolean;
  title: string;
  body: string;
  opsHint: string;
};

export const MATRIX_CLIENT_VERSIONS_PATH = '/_matrix/client/versions';

export function matrixClientVersionsUrl(homeserverUrl: string): string {
  return `${String(homeserverUrl || '').replace(/\/$/, '')}${MATRIX_CLIENT_VERSIONS_PATH}`;
}

/** Polar MATRIX_LABOR_CLIENT_PUBLIC_FED_CLOSED: nur HTTP 200 zählt. */
export function homeserverReachableFromHttpStatus(status: number): boolean {
  return status === 200;
}

/**
 * Probe GET {homeserver}/_matrix/client/versions.
 * Fail-closed: TLS/DNS/CORS/CSP/Timeout/≠200 → false. Nie true fake.
 * Labor-CSP connect-src muss matrix-labor erlauben (nginx-labor.conf), sonst catch → Karte „Homeserver tot“.
 */
export async function probeMatrixHomeserverReachable(
  homeserverUrl: string,
  fetchFn: typeof fetch = fetch,
): Promise<boolean> {
  const url = matrixClientVersionsUrl(homeserverUrl);
  if (!/^https:\/\//i.test(url)) return false;
  try {
    const res = await fetchFn(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      signal: AbortSignal.timeout(8000),
    });
    return homeserverReachableFromHttpStatus(res.status);
  } catch {
    return false;
  }
}

/**
 * Klasse DIKTAT_ELEMENT_HONEST_BLOCK + MATRIX_LABOR_CLIENT_PUBLIC_FED_CLOSED
 * Polar: VERBOTEN Phone-Sync-UI / Fake-iframe das E2EE still zieht / homeserverReachable=true ohne Probe-200.
 *        VERBOTEN Firma matrix.arkorbit.at / chat.arkorbit.at auf Labor-Diktat (URLs: MatrixView).
 *        VERBOTEN Silent-Fail tun als ginge Drag&Drop aus dem Element-iframe; window.open als Default; Federation.
 *        ERLAUBT echtes Element-Web-iframe (chat-labor / chat, host-aware); Datei laden; Probe 200.
 *        ERLAUBT ehrlicher Einzeiler: Datei in Element speichern, dann Dropzone/Upload (Cross-Origin).
 */
export const DIKTAT_ELEMENT_DND_HINT =
  'Aus Element Datei speichern, dann hierher ziehen oder hochladen.';

/** Element-Web Session-Lock (zweiter Tab auf chat-labor) — ehrlich, kein Session-Steal. */
export const DIKTAT_ELEMENT_SESSION_LOCK_HINT =
  'Wenn Element sagt, der Chat laufe schon in einem anderen Fenster: den anderen Tab schließen — der Kanzlei-Raum sitzt hier.';

export function assessElementDiktatLoad(args: {
  matrixRoomId?: string | null;
  isLaborHost: boolean;
  homeserverReachable?: boolean;
}): ElementDiktatLoadAssessment {
  const room = isHonestMatrixRoomId(args.matrixRoomId) ? String(args.matrixRoomId).trim() : '';
  const laborWithoutHomeserver = args.isLaborHost && !args.homeserverReachable;

  if (laborWithoutHomeserver) {
    return {
      kind: 'blocked_no_homeserver',
      canPickAudioFile: true,
      liveRoomPull: false,
      title: 'Kanzlei-Raum wartet auf den Chat-Server',
      body:
        'Der Chat-Server ist gerade nicht erreichbar. Sprachnachrichten aus der App werden hier nicht automatisch übernommen — Aufnahme und Datei-Upload gehen weiter.',
      opsHint:
        'Ops: DNS + Let’s Encrypt für matrix-labor.arkorbit.at und chat-labor.arkorbit.at (Federation bleibt zu). Sitze: element_seat + matrixRoomId (Mandantensitze — nicht in diesem Chat). Bis dahin: Mikrofon im Browser oder Audio-Datei (z. B. aus Element exportiert).',
    };
  }

  if (!room) {
    return {
      kind: 'blocked_no_room',
      canPickAudioFile: true,
      liveRoomPull: false,
      title: 'Kein Kanzlei-Raum hinterlegt',
      body: 'Für diesen Zugang ist noch kein Kanzlei-Raum hinterlegt. Sprachnachrichten aus der App werden hier nicht live abgeholt — Aufnahme und Datei-Upload gehen weiter.',
      opsHint:
        'Sitze/Onboarding: Element-Häkchen und matrixRoomId (nur office@ hat Labor-Testraum). Ops: Homeserver. Portal lädt bis dahin nur Aufnahme oder exportierte Datei.',
    };
  }

  return {
    kind: 'manual_file',
    canPickAudioFile: true,
    liveRoomPull: false,
    title: 'Von Element laden',
    body:
      'Chat läuft im Portal (Element). Sprachnachrichten sind Ende-zu-Ende verschlüsselt — das Portal zieht sie nicht still. Ziehen aus dem Element-Fenster geht im Browser nicht. Datei in Element speichern, dann hierher ziehen oder hochladen.',
    opsHint:
      'Live-Pull aus dem Raum folgt Sitze + Ops (Bot/Relay), nicht diesem Slice. Mic + Datei-Upload bleiben der Whisper-Pfad.',
  };
}

/**
 * iframe nur bei ehrlicher Room-Id + Load `manual_file`.
 * Polar: VERBOTEN Login-URL als Fallback wenn Room-Id leer/Stub.
 */
export function elementEmbedSrcForDiktat(args: {
  matrixRoomId?: string | null;
  loadKind: ElementDiktatLoadAssessment['kind'];
  hostname?: string;
}): string {
  if (args.loadKind !== 'manual_file') return '';
  return buildElementRoomUrl(String(args.matrixRoomId ?? ''), args.hostname);
}

/** Ordner im Diktat-Tab — Quelle = geladene PB-Liste, kein zweites Inventar. */
export const DIKTAT_ARCHIVE_MAX = 24;
export const DIKTAT_NOT_IN_AKTE_HINT = 'liegt unter Diktate, nicht in dieser Akte';
/** Explorer-Ordner unter portal-documents — gleicher Name wie in Dokumente. */
export const DIKTAT_PORTAL_FOLDER = 'Diktate';
/** Relativordner-Aliase (erste Pfadsegmente, lower-case). */
export const DIKTAT_PORTAL_FOLDER_ALIASES = ['diktate', 'audio-analyse', 'protokolle'] as const;
/** n8n `DIKTAT_UPLOAD` Dateiname, falls der PB-Record noch kein `nextcloud_path` hat. */
const DIKTAT_LEGACY_PROTOKOLL_NAME = /^Protokoll_\d{4}-\d{2}-\d{2}_\d+\.pdf$/i;
const DIKTAT_ANALYSE_TXT_NAME = /^Analyse_.+\.txt$/i;

export type DiktatArchiveEntry = {
  id: string;
  createdAt: string;
  sourceName: string;
  transcriptText: string;
  /** Protokoll, nur wenn es vom Transkript abweicht. Sitzung, kein zweites Dokument. */
  auswertungText?: string;
  pb_record_id?: string;
  nextcloud_path?: string;
  /** Nur echte PB-Relation — nie aus boundAkte oder NC-Pfad geraten. */
  akte_id?: string;
  pdfUrl?: string;
  filename?: string;
};

export type DiktatDokumenteCtaKind = 'show_in_explorer' | 'honest_not_in_akte';

/**
 * `akte_id` nur durchreichen, wenn der Record/Response sie wirklich hat.
 * Leerer String, Pfad oder gebundene Akte zählen nicht.
 */
export function optionalDiktatAkteId(raw: string | null | undefined): string | undefined {
  const value = String(raw || '').trim();
  return value || undefined;
}

export function diktatRecordInOpenAkteExplorer(
  pbRecordId: string | null | undefined,
  explorerPbIds: readonly string[],
): boolean {
  const id = String(pbRecordId || '').trim();
  if (!id) return false;
  return explorerPbIds.some((row) => row === id);
}

/**
 * Dokumente-CTA nur wenn der PB-Record in der aktuellen Explorer-Liste liegt
 * (offene Akte → nur deren Inventar; ohne Akte → geladene PB-Liste).
 */
export function diktatDokumenteCtaKind(args: {
  pbRecordId?: string | null;
  explorerPbIds: readonly string[];
}): DiktatDokumenteCtaKind {
  if (diktatRecordInOpenAkteExplorer(args.pbRecordId, args.explorerPbIds)) {
    return 'show_in_explorer';
  }
  return 'honest_not_in_akte';
}

/**
 * Dieselbe gemerkte Akte, die beim Wechsel Diktat → Akten wieder aufgeht.
 * Ohne diesen Eintrag oder ohne passende Zeile in der Liste: keine Akte, kein Knopf.
 */
export function diktatIntoRememberedAkteId(args: {
  rememberedAkteId: string;
  akten: ReadonlyArray<{ id: string }>;
  listReady: boolean;
}): string {
  const remembered = String(args.rememberedAkteId || '').trim();
  if (!remembered) return '';
  if (!args.listReady && args.akten.length === 0) return '';
  const hit = args.akten.find((row) => row.id === remembered);
  return hit ? hit.id : '';
}

/** Zielordner nur für die gemerkte Akte. Eine andere Akte bleibt leer. */
export function diktatIntoRememberedAkteFolder(args: {
  rememberedAkteId: string;
  akten: ReadonlyArray<{ id: string }>;
  listReady: boolean;
}): string {
  const akteId = diktatIntoRememberedAkteId(args);
  if (!akteId) return '';
  const safe = akteId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
  if (!safe || safe !== akteId) return '';
  return `akten/${safe}`;
}

export function diktatSuccessArchiveMessage(args: {
  transcript: string;
  cta: DiktatDokumenteCtaKind;
  serverMessage?: string;
}): string {
  const server = (args.serverMessage || '').trim();
  if (args.cta === 'show_in_explorer' && server) return server;
  if ((args.transcript || '').trim()) {
    return 'Transkription abgeschlossen. Das Transkript liegt unter Diktate.';
  }
  return DIKTAT_UPLOAD_NO_TEXT;
}

export function buildDiktatArchiveEntry(args: {
  sourceName: string;
  transcriptText: string;
  id?: string;
  createdAt?: string;
  pb_record_id?: string | null;
  nextcloud_path?: string | null;
  akte_id?: string | null;
  pdfUrl?: string | null;
  filename?: string | null;
  auswertungText?: string | null;
}): DiktatArchiveEntry | null {
  const transcript = (args.transcriptText || '').trim();
  if (!transcript) return null;
  const pb = String(args.pb_record_id || '').trim() || undefined;
  const nc = String(args.nextcloud_path || '').trim() || undefined;
  const akte_id = optionalDiktatAkteId(args.akte_id);
  const pdfUrl = String(args.pdfUrl || '').trim() || undefined;
  const filename = String(args.filename || '').trim() || undefined;
  const auswertungText = String(args.auswertungText || '').trim() || undefined;
  return {
    id: String(args.id || '').trim() || `diktat-${Date.now().toString(36)}`,
    createdAt: String(args.createdAt || '').trim() || new Date().toISOString(),
    sourceName: (args.sourceName || 'Diktat').trim() || 'Diktat',
    transcriptText: transcript,
    ...(pb ? { pb_record_id: pb } : {}),
    ...(nc ? { nextcloud_path: nc } : {}),
    ...(akte_id ? { akte_id } : {}),
    ...(pdfUrl ? { pdfUrl } : {}),
    ...(filename ? { filename } : {}),
    ...(auswertungText ? { auswertungText } : {}),
  };
}

/** PB-Felder nur mergen, wenn die Id wirklich zu diesem Eintrag gehört. */
export function attachDiktatPersistFromProtokoll(
  entry: DiktatArchiveEntry,
  proto:
    | {
        id?: string | null;
        akte_id?: string | null;
        nextcloud_path?: string | null;
        filename?: string | null;
      }
    | null
    | undefined,
): DiktatArchiveEntry {
  const entryPb = String(entry.pb_record_id || '').trim();
  const protoId = String(proto?.id || '').trim();
  if (!entryPb || !protoId || entryPb !== protoId) return entry;
  const akte_id = optionalDiktatAkteId(proto?.akte_id) || entry.akte_id;
  const nextcloud_path =
    String(proto?.nextcloud_path || '').trim() || entry.nextcloud_path;
  const filename = String(proto?.filename || '').trim() || entry.filename;
  return {
    ...entry,
    ...(nextcloud_path ? { nextcloud_path } : {}),
    ...(akte_id ? { akte_id } : {}),
    ...(filename ? { filename } : {}),
  };
}

export function mergeDiktatArchive(
  prev: readonly DiktatArchiveEntry[],
  entry: DiktatArchiveEntry,
  max = DIKTAT_ARCHIVE_MAX,
): DiktatArchiveEntry[] {
  return [entry, ...prev.filter((row) => row.id !== entry.id)].slice(0, max);
}

export function diktatArchiveHasFileHandle(entry: {
  pb_record_id?: string | null;
  nextcloud_path?: string | null;
  pdfUrl?: string | null;
}): boolean {
  return Boolean(
    String(entry.pb_record_id || '').trim() ||
      String(entry.nextcloud_path || '').trim() ||
      String(entry.pdfUrl || '').trim(),
  );
}

/** Nur echte PB-Zeilen als „PocketBase“; Session-only nicht so beschriften. */
export function diktatArchiveSourceLabel(args: {
  archive: readonly DiktatArchiveEntry[];
  loading: boolean;
}): string {
  const n = args.archive.length;
  if (args.loading && n === 0) return 'lädt…';
  const files = `${n} Datei${n === 1 ? '' : 'en'}`;
  if (n === 0) return files;
  const pb = args.archive.filter((row) => String(row.pb_record_id || '').trim()).length;
  if (pb === n) return `${files} · gespeichert`;
  if (pb === 0) return `${files} · diese Sitzung`;
  return `${files} · gespeichert und diese Sitzung`;
}

export type DiktatExplorerRowGate = {
  openDisabled: boolean;
  editDisabled: boolean;
  renameDisabled: boolean;
  shareDisabled: boolean;
  moveToAkteDisabled: boolean;
  deleteDisabled: boolean;
};

/**
 * Klasse DIKTAT_EDIT_WITH_PATH: echter NC-Pfad, kein Traversal, keine URL-als-Pfad.
 * Akte ist egal — Rename bleibt enger (Folder-API).
 */
export function diktatHasAllowedEditPath(nextcloudPath?: string | null): boolean {
  const path = String(nextcloudPath || '')
    .replace(/\\/g, '/')
    .trim();
  if (!path || path.includes('..') || /^https?:/i.test(path) || path.startsWith('//')) {
    return false;
  }
  const lower = path.toLowerCase();
  return (
    lower.includes('/protokolle/') ||
    lower.includes('/portal-documents/') ||
    /\/akten\/[^/]+\//i.test(path) ||
    /\/sitze\/[^/]+\//i.test(path) ||
    /\/(diktate|audio-analyse)\//i.test(path)
  );
}

/**
 * Klasse DIKTAT_OUTPUT_FORMAT: .txt nicht als OnlyOffice-Edit verkaufen.
 * Bearbeiten auf TXT öffnet das Format-Menü (DOCX zum Weiterarbeiten).
 */
export function diktatEditAction(args: {
  nextcloudPath?: string | null;
  filename?: string | null;
  openableInEditor: boolean;
}): DiktatEditAction {
  const pathOk = diktatHasAllowedEditPath(args.nextcloudPath);
  const kind = diktatFileKind(args.filename, args.nextcloudPath);
  if (kind === 'txt' && pathOk) return 'pick_format';
  if (!pathOk) return 'blocked';
  if (kind === 'pdf') return args.openableInEditor ? 'pdf_viewer' : 'blocked';
  if (kind === 'docx') return args.openableInEditor ? 'onlyoffice' : 'blocked';
  return args.openableInEditor ? 'onlyoffice' : 'blocked';
}

/** Inhalt: Pfad + passende Aktion — TXT = Formatwahl, nicht Fake-OO. */
export function diktatCanEditWithPath(args: {
  nextcloudPath?: string | null;
  openableInEditor: boolean;
  filename?: string | null;
}): boolean {
  return diktatEditAction(args) !== 'blocked';
}

/**
 * Explorer-Aktionen: Datei-Handle sticht, nicht akte_id.
 * Umbenennen bleibt an portal-documents gebunden (Folder-API).
 * Bearbeiten (Inhalt) folgt DIKTAT_EDIT_WITH_PATH — Pfad, nicht Akte.
 * TXT: Menü statt OnlyOffice (DIKTAT_OUTPUT_FORMAT).
 */
export function diktatExplorerRowGate(args: {
  canWrite: boolean;
  hasPbDoc: boolean;
  nextcloudPath?: string | null;
  filename?: string | null;
  documentUrl?: string | null;
  pbRecordId?: string | null;
  openableInEditor: boolean;
}): DiktatExplorerRowGate {
  const path = String(args.nextcloudPath || '').trim();
  const url = String(args.documentUrl || '').trim();
  const pb = String(args.pbRecordId || '').trim();
  const handle = Boolean(pb || path || url);
  const underPortalDocuments = /portal-documents/i.test(path.replace(/\\/g, '/'));
  return {
    openDisabled: !handle && !args.openableInEditor,
    editDisabled: !diktatCanEditWithPath({
      nextcloudPath: path,
      filename: args.filename,
      openableInEditor: args.openableInEditor,
    }),
    renameDisabled: !args.canWrite || !underPortalDocuments,
    shareDisabled: !args.canWrite || !args.hasPbDoc,
    moveToAkteDisabled: !args.canWrite || !args.hasPbDoc,
    deleteDisabled: !args.canWrite || !pb,
  };
}

/** PB-Zeile aus Archiveintrag, falls die Hub-Liste den Record noch nicht hat. */
export function protokollFromDiktatArchiveEntry(
  entry: DiktatArchiveEntry,
): {
  id: string;
  filename: string;
  nc_url: string;
  nextcloud_path?: string;
  created: string;
  akte_id?: string;
} | undefined {
  const id = String(entry.pb_record_id || '').trim();
  if (!id) return undefined;
  const nc = String(entry.nextcloud_path || '').trim() || undefined;
  const url = String(entry.pdfUrl || '').trim();
  const akte_id = optionalDiktatAkteId(entry.akte_id);
  return {
    id,
    filename: String(entry.filename || entry.sourceName || 'Diktat').trim() || 'Diktat',
    nc_url: url,
    created: String(entry.createdAt || '').trim() || new Date().toISOString(),
    ...(nc ? { nextcloud_path: nc } : {}),
    ...(akte_id ? { akte_id } : {}),
  };
}

/**
 * Diktat-PDF/TXT aus derselben PB-Liste wie Dokumente.
 * `/protokolle/` = Whisper-PDF. `portal-documents/Diktate|audio-analyse/` = Explorer-Ordner.
 * `Analyse_*.txt` = Hintergrundanalyse. Legacy ohne Pfad: n8n-Dateiname, nie wenn schon `akte_id`.
 */
export function diktatRelFolderFromNcPath(nextcloudPath?: string | null): string {
  const path = String(nextcloudPath || '').replace(/\\/g, '/');
  const sitze = path.match(/\/Mandanten\/[^/]+\/sitze\/[^/]+\/(.+)$/i);
  if (sitze) {
    const parts = sitze[1].split('/').filter(Boolean);
    if (parts.length < 1) return '';
    parts.pop();
    return parts.join('/');
  }
  const marker = '/portal-documents/';
  const idx = path.toLowerCase().indexOf(marker);
  if (idx < 0) return '';
  const parts = path
    .slice(idx + marker.length)
    .split('/')
    .filter(Boolean);
  if (parts.length < 2) return '';
  parts.pop();
  return parts.join('/');
}

export function isDiktatExplorerRelFolder(relFolder: string): boolean {
  const first = relFolder.split('/').filter(Boolean)[0];
  if (!first) return false;
  return (DIKTAT_PORTAL_FOLDER_ALIASES as readonly string[]).includes(first.toLowerCase());
}

export function isDiktatPortalDocument(doc: {
  filename?: string | null;
  nextcloud_path?: string | null;
  akte_id?: string | null;
}): boolean {
  const path = String(doc.nextcloud_path || '')
    .replace(/\\/g, '/')
    .toLowerCase();
  const filename = String(doc.filename || '').trim();
  if (path.includes('/protokolle/')) return true;
  if (isDiktatExplorerRelFolder(diktatRelFolderFromNcPath(doc.nextcloud_path))) return true;
  if (DIKTAT_ANALYSE_TXT_NAME.test(filename)) return true;
  if (optionalDiktatAkteId(doc.akte_id)) return false;
  if (path) return false;
  return DIKTAT_LEGACY_PROTOKOLL_NAME.test(filename);
}

export function diktatArchiveEntryFromProtokoll(
  doc: {
    id: string;
    filename: string;
    created: string;
    nc_url?: string;
    nextcloud_path?: string;
    akte_id?: string;
  },
  transcriptText?: string,
): DiktatArchiveEntry {
  const akte_id = optionalDiktatAkteId(doc.akte_id);
  const nc = String(doc.nextcloud_path || '').trim() || undefined;
  const pdfUrl = String(doc.nc_url || '').trim() || undefined;
  return {
    id: doc.id,
    createdAt: String(doc.created || '').trim() || new Date().toISOString(),
    sourceName: (doc.filename || 'Diktat').trim() || 'Diktat',
    transcriptText: (transcriptText || '').trim(),
    pb_record_id: doc.id,
    filename: doc.filename,
    ...(nc ? { nextcloud_path: nc } : {}),
    ...(akte_id ? { akte_id } : {}),
    ...(pdfUrl ? { pdfUrl } : {}),
  };
}

/**
 * Hard-Reload: PB-Zeilen sind die Liste. Session-Transkript an PB-Zeile hängen
 * (pb_id, sonst Dateiname-Stamm) — Ghost ohne Handle fällt weiter weg.
 * Kein sessionStorage.
 */
export function hydrateDiktatArchiveFromProtokolle(
  session: readonly DiktatArchiveEntry[],
  protokolle: readonly {
    id: string;
    filename: string;
    created: string;
    nc_url?: string;
    nextcloud_path?: string;
    akte_id?: string;
  }[],
  max = DIKTAT_ARCHIVE_MAX,
): DiktatArchiveEntry[] {
  const diktatRows = protokolle.filter(isDiktatPortalDocument);
  const sessionByPb = new Map<string, DiktatArchiveEntry>();
  for (const row of session) {
    const pb = String(row.pb_record_id || '').trim();
    if (pb) sessionByPb.set(pb, row);
  }
  const fromPb = diktatRows.map((doc) => {
    const prev = sessionByPb.get(doc.id);
    const built = diktatArchiveEntryFromProtokoll(
      doc,
      diktatUsableTranscript(prev?.transcriptText) ||
        resolveDiktatOutputTranscript({
          entry: { filename: doc.filename, sourceName: doc.filename, pb_record_id: doc.id },
          archive: session,
          minScore: 50,
        }),
    );
    const auswertungText = String(prev?.auswertungText || '').trim();
    return auswertungText ? { ...built, auswertungText } : built;
  });
  const fromPbIds = new Set(fromPb.map((row) => row.pb_record_id || row.id));
  const pending = session.filter((row) => {
    const pb = String(row.pb_record_id || '').trim();
    if (pb) return !fromPbIds.has(pb);
    const path = String(row.nextcloud_path || '').trim();
    const url = String(row.pdfUrl || '').trim();
    if (!path && !url) return false;
    return !diktatRows.some(
      (doc) =>
        (path && String(doc.nextcloud_path || '').trim() === path) ||
        (url && String(doc.nc_url || '').trim() === url),
    );
  });
  const merged = [...pending, ...fromPb];
  merged.sort((a, b) => {
    const tb = Date.parse(b.createdAt) || 0;
    const ta = Date.parse(a.createdAt) || 0;
    return tb - ta || b.id.localeCompare(a.id);
  });
  const seen = new Set<string>();
  const unique: DiktatArchiveEntry[] = [];
  for (const row of merged) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    unique.push(row);
    if (unique.length >= max) break;
  }
  return unique;
}
